import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import AppConst from '../apis/appconst';   // <-- FIX THE RELATIVE PATH to match this file's location

export const THEME = {
  // ---- colors ----
  headerBg:  [180, 219, 250],
  headerTxt: [0, 0, 0],
  border:    [0, 0, 0],


  flags: {
    high:       { label: '', color: [0, 0, 0]   },  // red
    low:        { label: '', color: [0, 0, 0]   },  // blue
    borderline: { label: '', color: [0, 0, 0]  },  // orange
    bold:       1,          // 1 = flagged results also bold
    boldColor:  [0, 0, 0]   // color for the manual is_bold checkbox
  },

  layout: {
    headerHeight:     30,
    topMarginWith:    40,
    topMarginWithout: 40,
    sideMargin:       14,
    signatureReserve: 40
  },


  // ---- outlines: 1 = draw, 0 = hide ----
  outline: {
    patientBox:   1,   // patient details box
    testTable:    0,   // box around each test's result table  for test outline 
    deptHeader:   1,   // department band border
    interpBox:    1,   // Interpretation / Impression / Notes box
    radius:       0   // corner rounding (0 = square)
  },

  // ---- barcode: 1 = show, 0 = hide ----
  
  // ---- font sizes ----
  font: {
    deptHeader:   12,  // department band
    tableHead:    9,   // TEST NAME / RESULT / ...
    tableBody:    9,   // param rows
    testName:     9,   // main test name row
    sectionTitle: 9,   // "Interpretation" / "Impression" / "Notes:"
    editorTitle:  10,  // Editor-type param heading
    patient:      10,  // patient details box
    barcodeText:  7,
    endOfReport:  12,
    pageNumber:   8
  },

 qr: {
  show:    1,
  size:    17,     // pt (square)
  gapTop:  -3,      // + moves QR down, − moves it up (from centered)
  gapRight: 3,     // distance from the box's right edge
  scale:   4,
  margin:  0,
  ecLevel: 'M'
},

  barcode: { show: 0, width: 30, height: 6, gapTop: 1.3, showText: 1 },


genderRange: {
  enabled:         1,   // 1 = auto-evaluate M/F ranges for Text params
  showOnlyMatched: 0    // 1 = print only the patient's range in the PDF column
},
  // ---- column alignment + widths (must total ~191) ----
  columns: {
    testName: { width: 50, halign: 'left'   },
    result:   { width: 36, halign: 'left' },
    unit:     { width: 22, halign: 'left' },
    range:    { width: 43, halign: 'left' },
    method:   { width: 40, halign: 'left' }
  }
};

/* =====================================================================
   LOAD THEME FROM BACKEND
   THEME above stays as the DEFAULT / fallback. This fetches the saved
   Report Configuration and overwrites THEME in place. If the request
   fails, the defaults above are used, so the report never breaks.

   >>> YOU MUST await this ONCE before building the PDF <<<
       await loadThemeConfig();   // then prepareCodes(), then draw
   ===================================================================== */

// Use the SAME base + route your app uses everywhere else.
// (Add getReportConfigUrl to appconst — e.g. "get_report_config".)
const CONFIG_URL = AppConst.BASE_URL + AppConst.getReportConfigUrl;

// deep-merge fetched values into the existing THEME object (mutates in place).
// arrays like [r,g,b] are replaced whole; nested objects merge key-by-key.
const _mergeInto = (target, src) => {
  for (const k of Object.keys(src || {})) {
    const sv = src[k], tv = target[k];
    if (sv && typeof sv === 'object' && !Array.isArray(sv) &&
        tv && typeof tv === 'object' && !Array.isArray(tv)) {
      _mergeInto(tv, sv);
    } else {
      target[k] = sv;
    }
  }
  return target;
};

let _themeLoaded = false;
export const loadThemeConfig = async (key = 'default') => {
  if (_themeLoaded) return THEME;                 // fetch only once per page load
  try {
    const url = `${CONFIG_URL}?key=${encodeURIComponent(key)}`;
    console.debug('[theme] fetching config from:', url);   // <-- check this in DevTools
    const res  = await fetch(url, { headers: { Accept: 'application/json' } });
    const json = await res.json();
    console.debug('[theme] config response:', json);       // <-- must contain { theme: {...} }
    if (json && json.theme) {
      _mergeInto(THEME, json.theme);
      _themeLoaded = true;
    } else {
      console.warn('[theme] response had no "theme" key — using defaults.', json);
    }
  } catch (e) {
    console.warn('[theme] config load failed — using defaults.', e);
  }
  return THEME;
};

// Optional: call this after SAVING new config so the next PDF re-fetches.
export const resetThemeCache = () => { _themeLoaded = false; };

/* ---------- shared helpers ---------- */

export const drawOutline = (doc, enabled, x, y, w, h, style = 'S') => {
  if (!enabled) return;
  const X = +x, Y = +y, W = +w, H = +h;
  if (![X, Y, W, H].every(Number.isFinite) || W <= 0 || H <= 0) return;
  doc.setDrawColor(...THEME.border);
  doc.setLineWidth(0.2);
  const r = Number(THEME.outline.radius) || 0;
  if (r > 0) doc.roundedRect(X, Y, W, H, r, r, style);
  else doc.rect(X, Y, W, H, style);
};

export const evaluateFlag = (resultRaw, min, max) => {
  const v = parseFloat(resultRaw);
  if (isNaN(v) || isNaN(min) || isNaN(max)) return null;
  const lo = Math.min(min, max), hi = Math.max(min, max), EPS = 1e-9;
  if (Math.abs(v - hi) < EPS || Math.abs(v - lo) < EPS) return THEME.flags.borderline;
  if (v > hi) return THEME.flags.high;
  if (v < lo) return THEME.flags.low;
  return null;
};

export const getAgeDisplay = (p) => {
  if (!p) return 'N/A';
  const y = +(p.lab_reg_age_year || 0), m = +(p.lab_reg_age_month || 0), d = +(p.lab_reg_age_day || 0);
  if (!y && !m && !d) return 'N/A';
  const parts = [];
  if (y > 0) parts.push(`${y}Y${y > 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m}M${m > 1 ? 's' : ''}`);
  if (d > 0) parts.push(`${d}D${d > 1 ? 's' : ''}`);
  return parts.join(' ');
};
// Call ONCE before building the PDF. Returns plain dataURLs.
export const prepareCodes = async (patient) => {
  const [qr, barcode] = await Promise.all([
    THEME.qr.show ? makeQr(patient) : Promise.resolve(null),
    Promise.resolve(THEME.barcode.show ? makeBarcode(patient?.lab_reg_uhid) : null)
  ]);
  return { qr, barcode };
};

/* ---------- QR: SHORT payload, scannable ---------- */
// export const buildQrText = (patient = {}) => {
//   const title = patient.lab_reg_name_title ? String(patient.lab_reg_name_title).trim() + ' ' : '';
//   const lines = [
//     `UHID:${patient.lab_reg_uhid || ''}`,
//     `PID:${patient.lab_reg_id || ''}`,
//     `Name:${title}${patient.lab_reg_name || ''}`,
//     `Age:${getAgeDisplay(patient)}/${(patient.lab_reg_gender || '').charAt(0)}`
//   ];
//   if (patient.lab_reg_sample_datetime) {
//     lines.push(`Sample:${String(patient.lab_reg_sample_datetime).split(' ')[0]}`);
//   }
//   return lines.join('\n');
// };



// Patient-facing report link base, derived from AppConst.
// Prefers PUBLIC_APP_URL, then ONLINE_URL, then BASE_URL.
const _publicBase = () => {
  try {
    const raw = (AppConst && (AppConst.PUBLIC_APP_URL || AppConst.ONLINE_URL || AppConst.BASE_URL)) || '';
    return String(raw).replace(/\/+$/, '');
  } catch (e) { return ''; }
};

export const buildQrText = (patient = {}) => {
  const token = patient.lab_reg_report_token || '';
  const base  = _publicBase();
  if (token && base) return `${base}/r/${token}`;

  // no token or no public base -> plain text so the QR is never empty
  const title = patient.lab_reg_name_title ? String(patient.lab_reg_name_title).trim() + ' ' : '';
  return [
    `UHID:${patient.lab_reg_uhid || ''}`,
    `Name:${title}${patient.lab_reg_name || ''}`
  ].join('\n');
};

const _qrCache = {};
export const makeQr = async (patient) => {
  if (!THEME.qr.show) return null;
  const text = buildQrText(patient);
  if (_qrCache[text]) return _qrCache[text];
  try {
    const url = await QRCode.toDataURL(text, {
      errorCorrectionLevel: THEME.qr.ecLevel,
      margin: THEME.qr.margin,
      scale: THEME.qr.scale
    });
    _qrCache[text] = url;
    return url;
  } catch (e) {
    console.warn('QR generation failed', e);
    return null;
  }
};

const _barcodeCache = {};


/* ---------- gender-aware ranges ---------- */

export const normalizeGender = (g) => {
  const s = String(g || '').trim().toLowerCase();
  if (s.startsWith('m')) return 'M';
  if (s.startsWith('f')) return 'F';
  return null;
};

export const parseGenderRanges = (rangeRaw) => {
  const s = String(rangeRaw || '').replace(/[\u2013\u2014]/g, '-').trim();
  if (!s) return null;

  const re = /\b(m|male|f|female)\b\s*[:=-]?\s*([\d.]+)\s*(?:-|to)\s*([\d.]+)/gi;
  const out = {};
  let m;
  while ((m = re.exec(s)) !== null) {
    const key = normalizeGender(m[1]);
    const lo = parseFloat(m[2]), hi = parseFloat(m[3]);
    if (!key || isNaN(lo) || isNaN(hi)) continue;
    out[key] = { min: Math.min(lo, hi), max: Math.max(lo, hi) };
  }
  return Object.keys(out).length ? out : null;
};

export const evaluateFlagSmart = (resultRaw, rangeText, patient) => {
  if (isNaN(parseFloat(resultRaw))) return null;
  const s = String(rangeText || '').replace(/[\u2013\u2014]/g, '-').trim();

  const simple = s.match(/^\s*([\d.]+)\s*-\s*([\d.]+)\s*$/);
  if (simple) return evaluateFlag(resultRaw, parseFloat(simple[1]), parseFloat(simple[2]));

  if (!THEME.genderRange.enabled) return null;
  const g = normalizeGender(patient?.lab_reg_gender);
  if (!g) return null;

  const byG = parseGenderRanges(s);
  if (!byG || !byG[g]) return null;
  return evaluateFlag(resultRaw, byG[g].min, byG[g].max);
};

export const genderRangeDisplay = (rangeText, patient) => {
  if (!THEME.genderRange.showOnlyMatched) return rangeText;
  const g = normalizeGender(patient?.lab_reg_gender);
  const byG = parseGenderRanges(rangeText);
  if (!g || !byG || !byG[g]) return rangeText;
  return `${byG[g].min} - ${byG[g].max}`;
};


export const makeBarcode = (uhid) => {
  const val = String(uhid || '').trim();
  if (!val) return null;
  if (_barcodeCache[val]) return _barcodeCache[val];
  try {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, val, {
      format: 'CODE128', displayValue: false, margin: 0, width: 2, height: 60
    });
    const url = canvas.toDataURL('image/png');
    _barcodeCache[val] = url;
    return url;
  } catch (e) {
    console.warn('barcode generation failed', e);
    return null;
  }
};





export async function buildReportPdf({
  
  jsPDF,
  autoTable,
  loadFontAsBase64,
  tests,
  patient,
  printTypeOpt = 'with',
  departs = [],
  signData = {},
  userName = '',
  images = {},
  reportedOn = null,
  publicUrl = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '',
}) {
  console.log('>>> SIGN 32 obj:', signData['32']);          // ✅ inside the function
  console.log('>>> SIGN 60 obj:', signData['60']);
  await loadThemeConfig();

  const doc = new jsPDF();
  const Name = userName || '';
  const headerImg    = images.header || '';
  const watermarkImg = images.watermark || '';
  const footerImg    = images.footer || '';

  // ---- image cache + fetch helper (self-contained) ----
  const _imgCache = {};
  const imageUrlToBase64 = async (url) => {
    if (!url) return null;
    if (_imgCache[url]) return _imgCache[url];
    try {
      const response = await fetch(url, { cache: 'no-cache' });
      if (!response.ok) return null;
      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) return null;
      const result = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      _imgCache[url] = result;
      return result;
    } catch (e) {
      console.warn('imageUrlToBase64 failed', url, e);
      return null;
    }
  };

  // ---- fonts ----
  let fontOk = false, boldOk = false;
  try {
    let f = await loadFontAsBase64(`${publicUrl}/DejaVuSans.ttf`);
    f = String(f).replace(/^data:.*;base64,/, '');
    if (!f || f.length < 5000) throw new Error('bad');
    doc.addFileToVFS('DejaVuSans.ttf', f);
    doc.addFont('DejaVuSans.ttf', 'DejaVu', 'normal');
    doc.setFont('DejaVu', 'normal'); doc.getTextWidth('A'); fontOk = true;
  } catch (e) { fontOk = false; }
  try {
    let b = await loadFontAsBase64(`${publicUrl}/DejaVuSans-Bold.ttf`);
    b = String(b).replace(/^data:.*;base64,/, '');
    if (!b || b.length < 5000) throw new Error('bad');
    doc.addFileToVFS('DejaVuSans-Bold.ttf', b);
    doc.addFont('DejaVuSans-Bold.ttf', 'DejaVu', 'bold');
    doc.setFont('DejaVu', 'bold'); doc.getTextWidth('A'); boldOk = true;
  } catch (e) { boldOk = false; }
  doc.setFont(fontOk ? 'DejaVu' : 'helvetica', 'normal');

  if (!patient) throw new Error('buildReportPdf: no patient');

  const codes = await prepareCodes(patient);
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();

  // ---- layout (single-driver, config-aware) ----
  const L = THEME.layout || {};
  const SIGNATURE_AREA_HEIGHT = Number(L.signatureReserve ?? 40);
  const SIDE_MARGIN = Number(L.sideMargin ?? 14);
  const HEADER_H = Number(L.headerHeight ?? 30);
  const PATIENT_BOX_Y = printTypeOpt === 'with'
    ? Number(L.topMarginWith ?? (HEADER_H + 10))
    : Number(L.topMarginWithout ?? 10);
  const PATIENT_BOX_HEIGHT = 26;
  const TOP_SAFE_Y = PATIENT_BOX_Y + PATIENT_BOX_HEIGHT + 6;
  const SAFE_BOTTOM = pageHeight - SIGNATURE_AREA_HEIGHT;
  const BOX_BOTTOM = pageHeight - SIGNATURE_AREA_HEIGHT - 10;
  const TABLE_BOTTOM_MARGIN = SIGNATURE_AREA_HEIGHT + 12;
  const TABLE_TOP_MARGIN = TOP_SAFE_Y + 8;
  const TEXT_PAGE_BUFFER = SIGNATURE_AREA_HEIGHT + 12;

  let hasRadiology = false;
  let currentPageNumber = 1;
  let lastPrintedDeptOnCurrentPage = null;
  let yPos = 0;
    const pageDept = {};
  let currentGroupDept = 'path';
  let currentTestStamp = null;    // reported time of the test being rendered
  const pageStamp = {};           // { [pageNumber]: stamp }  // which group is being printed right now

  // ---- date helpers ----
  const formatDateTime12h = (s) => {
    if (!s) return '';
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      const dd = String(d.getDate()).padStart(2, '0');
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const yy = d.getFullYear();
      let hh = d.getHours();
      const mm = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      const ap = hh >= 12 ? 'PM' : 'AM';
      hh = hh % 12 || 12;
      return `${dd}-${mo}-${yy} ${String(hh).padStart(2, '0')}:${mm}:${ss} ${ap}`;
    } catch (e) { return s; }
  };

  const formatCellValue = (htmlString) => {
    const d = document.createElement('div');
    d.innerHTML = htmlString;
    const table = d.querySelector('table');
    if (table) {
      const rows = [];
      for (const tr of table.querySelectorAll('tr')) {
        const cells = [];
        for (const td of tr.querySelectorAll('td')) cells.push(td.innerText.trim());
        rows.push(cells.join(' | '));
      }
      return rows.join('\n');
    }
    return d.innerText.trim();
  };

  const get_param_val = (tmp) => {
    if (!tmp) return '';
    const type = (tmp.test_param_type || '').trim();
    if (type === 'Numeric') {
      const from = tmp.test_param_from_range ?? '';
      const to = tmp.test_param_to_range ?? '';
      if (from === '' && to === '') return '';
      if (from !== '' && to !== '') return `${from} - ${to}`;
      return from !== '' ? `${from}` : `${to}`;
    }
    if (type === 'Boolean') return tmp.test_param_from_range || 'Yes / No';
    return tmp.test_param_from_range || '';
  };

  // ---- header / footer / watermark ----
   // ---- header / footer / watermark ----
  // images resolved ONCE up front so drawing is synchronous and can't land on the wrong page
  const headerB64    = printTypeOpt === 'with' ? await imageUrlToBase64(headerImg)    : null;
  const watermarkB64 = printTypeOpt === 'with' ? await imageUrlToBase64(watermarkImg) : null;
  const footerB64    = printTypeOpt === 'with' ? await imageUrlToBase64(footerImg)    : null;

  const addHeaderAndFooter = () => {
    if (printTypeOpt !== 'with') return;
     console.log('header drawn on page', doc.internal.getCurrentPageInfo().pageNumber);
    try {
      if (watermarkB64 && typeof doc.setGState === 'function') {
        doc.setGState(new doc.GState({ opacity: 0.08 }));
        doc.addImage(watermarkB64, 'PNG', (pageWidth - 120) / 2, (pageHeight - 120) / 2, 120, 120);
        doc.setGState(new doc.GState({ opacity: 1 }));
      }
    } catch (e) {}
    try { if (headerB64) doc.addImage(headerB64, 0, 0, pageWidth, HEADER_H); } catch (e) {}
    try { if (footerB64) doc.addImage(footerB64, 0, pageHeight - 13, pageWidth, 13); } catch (e) {}
  };

  // ---- patient details box ----
    const printPatientDetails = (startY, draw = true) => {
    try {
      startY = Number(startY); if (!isFinite(startY)) startY = PATIENT_BOX_Y;
      const PAGE_WIDTH = pageWidth;
      const boxLeft = 10, boxTop = startY - 2;
      const boxWidth = PAGE_WIDTH - boxLeft * 2, boxHeight = 26;
          // wipe the box area first so a repaint doesn't stack text
            if (!draw) {
        // layout only — return where content starts, draw nothing
        let skipAfter = boxTop + boxHeight;
        if (THEME.barcode.show && codes.barcode) {
          skipAfter += Number(THEME.barcode.gapTop) + Number(THEME.barcode.height)
                     + (THEME.barcode.showText ? 3 : 0);
        }
        return skipAfter + 6;
      }
      drawOutline(doc, THEME.outline.patientBox, boxLeft, boxTop, boxWidth, boxHeight);

      if (THEME.qr.show && codes.qr) {
        const qrSize = Number(THEME.qr.size);
        const gapRight = Number(THEME.qr.gapRight ?? 3);
        const gapTop = Number(THEME.qr.gapTop ?? 0);
        const qrX = boxLeft + boxWidth - qrSize - gapRight;
        const qrY = boxTop + (boxHeight - qrSize) / 2 + gapTop;
        try { doc.addImage(codes.qr, 'PNG', qrX, qrY, qrSize, qrSize); } catch (e) {}
      }

      const dp = String(patient.lab_reg_date || '').split('-');
      const formattedDate = dp.length >= 3 ? `${dp[2]}-${dp[1]}-${dp[0]}` : (patient.lab_reg_date || '');
                    const pg = doc.internal.getCurrentPageInfo().pageNumber;
      const pageTime = pageStamp[pg] || currentTestStamp || reportedOn || new Date();
      const reportedOnText = formatDateTime12h(pageTime);
      const sampleCollectedOn = patient.lab_reg_sample_datetime ? formatDateTime12h(patient.lab_reg_sample_datetime) : 'N/A';
      const titlePrefix = patient.lab_reg_name_title ? String(patient.lab_reg_name_title).trim() + ' ' : '';

      const lcl = boxLeft + 4, lcv = lcl + 36;
      const rcl = boxLeft + Math.round(boxWidth * 0.5) + 4, rcv = rcl + 36;
      let y = boxTop + 5;
      doc.setFontSize(THEME.font.patient); doc.setTextColor(0, 0, 0);

      const row = (lLabel, lVal, rLabel, rVal) => {
        doc.setFont('helvetica', 'bold');   doc.text(lLabel, lcl, y);
        doc.setFont('helvetica', 'normal'); doc.text(`: ${lVal}`, lcv, y);
        doc.setFont('helvetica', 'bold');   doc.text(rLabel, rcl, y);
        doc.setFont('helvetica', 'normal'); doc.text(`: ${rVal}`, rcv, y);
        y += 4;
      };
      row('UHID', patient.lab_reg_uhid || '', 'Patient ID', `00${patient.lab_reg_id || ''}`);
      row('Patient Name', `${titlePrefix}${patient.lab_reg_name || ''}`, 'Registered On', formattedDate);
      row('Age / Gender', `${getAgeDisplay(patient)} / ${patient.lab_reg_gender || ''}`, 'Collected At', patient.lab_collected_at || '');
            // Referred By — wrap long doctor names within the left column
            {
              const refLabelX = lcl, refValX = lcv;
              const colRightEdge = boxLeft + Math.round(boxWidth * 0.5); // where right column starts
              const maxValW = colRightEdge - refValX - 2;                // room for the value
              const refText = `: ${patient.lab_reg_referral || 'N/A'}`;
              const refLines = doc.splitTextToSize(refText, Math.max(10, maxValW));
      
              doc.setFont('helvetica', 'bold');   doc.text('Referred By', refLabelX, y);
              doc.setFont('helvetica', 'normal'); doc.text(refLines, refValX, y);
      
              // right column stays single-line, aligned to first line
              doc.setFont('helvetica', 'bold');   doc.text('Sample Collected By', rcl, y);
              doc.setFont('helvetica', 'normal'); doc.text(`: ${Name || ''}`, rcv, y);
      
              // advance by however many lines the referral wrapped to (min 1)
              y += 4 * Math.max(1, refLines.length);
            }
      row('Sample Collection', sampleCollectedOn, 'Reported On', reportedOnText);   // 3c

      let afterBox = boxTop + boxHeight;
      if (THEME.barcode.show && codes.barcode) {
        const bw = Number(THEME.barcode.width), bh = Number(THEME.barcode.height);
        const bx = boxLeft + boxWidth - bw, by = afterBox + Number(THEME.barcode.gapTop);
        try {
          doc.addImage(codes.barcode, 'PNG', bx, by, bw, bh);
          afterBox = by + bh;
          if (THEME.barcode.showText) {
            doc.setFontSize(THEME.font.barcodeText).setFont('helvetica', 'normal').setTextColor(0, 0, 0);
            doc.text(String(patient.lab_reg_uhid || ''), bx + bw, by + bh + 3, { align: 'right' });
            afterBox += 3;
          }
        } catch (e) {}
      }
      return afterBox + 6;
    } catch (err) {
      console.error('printPatientDetails failed', err);
      return Number(startY) + 9;
    }
  };

  // ---- signatures ----
  const addSignatures = async (sd) => {
    const finalY = pageHeight - 48;
    if (!sd) return;

    // present = the field exists and isn't an empty string.
    // A space (" ") counts as present — only truly empty/null is skipped.
       // a name counts only if it has a visible (non-whitespace, non-&nbsp;) character
       // show if the field is non-empty. A deliberate space counts; only "" / null / undefined hide it.
       const techName = sd.sign_tech_name != null ? String(sd.sign_tech_name) : '';
       const docName  = sd.sign_doc_name  != null ? String(sd.sign_doc_name)  : '';
       const hasTech = techName.length > 0;   // " " has length 1 → shows
       const hasDoc  = docName.length  > 0;

    // ---- Lab Technician ----
   // ---- Lab Technician ----
if (hasTech) {
  if (sd.sign_tech_sign) {
    const t = await imageUrlToBase64(AppConstBase() + sd.sign_tech_sign);
    if (t) doc.addImage(t, 'PNG', 14, finalY, 30, 15);
  }
  const id = sd.sign_tech_id ? ` (${sd.sign_tech_id})` : '';
  doc.setFontSize(9).setFont('helvetica', 'normal').text(`${techName}${id}`, 14, finalY + 20);

  // use sign_speci (fallback to qualification, then a default label)
  const techDesig = sd.sign_speci || sd.sign_quali || '';
  doc.setFontSize(9).setFont('helvetica', 'bold').text(techDesig, 14, finalY + 24);
}

    // ---- Doctor ----
    if (hasDoc) {
      if (sd.sign_doc_sign) {
        const d2 = await imageUrlToBase64(AppConstBase() + sd.sign_doc_sign);
        if (d2) doc.addImage(d2, 'PNG', 166, finalY, 30, 15);
      }
      const id = sd.sign_doc_id ? ` (${sd.sign_doc_id})` : '';
      doc.setFontSize(11).setFont('helvetica', 'bold').text(`${docName}${id}`, 196, finalY + 20, { align: 'right' });
      const desig = sd.sign_doc_spec || sd.sign_doc_quali || '';
      doc.setFontSize(9).setFont('helvetica', 'bold').text(desig, 196, finalY + 24, { align: 'right' });
    }
  };

  // ---- html renderer (text + bold/italic + lists + IMAGES) ----
  const drawHtmlContent = async (htmlString, initialX, initialY, options) => {
    const { maxWidth, addNewPage, pageBuffer } = options;
    let x = initialX, y = initialY;
    const styleStack = [{ font: 'helvetica', style: 'normal', size: 9 }];
    const applyStyle = () => {
      const s = styleStack[styleStack.length - 1];
      doc.setFontSize(s.size); doc.setFont(s.font, s.style);
    };
    const parseNodes = async (nodes) => {
      for (const node of nodes) {
        if (y > pageHeight - pageBuffer) { y = await addNewPage(); x = initialX; }
        if (node.nodeType === 3) {
          const raw = node.nodeValue; if (!raw) continue;
          const text = raw.replace(/\s+/g, ' '); if (text === '') continue;
          const words = text.split(' ');
          applyStyle();
          const sw = doc.getTextWidth(' ');
          for (const word of words) {
            if (word === '') { x += sw; continue; }
            const ww = doc.getTextWidth(word);
            if (x + ww > initialX + maxWidth) {
              x = initialX; y += 5;
              if (y > pageHeight - pageBuffer) { y = await addNewPage(); }
              applyStyle();
            }
            doc.text(word, x, y); x += ww + sw;
          }
        } else if (node.nodeType === 1) {
          const tagName = node.tagName.toUpperCase();

          if (tagName === 'IMG') {
            const src = node.getAttribute && node.getAttribute('src');
            if (src) {
              const dataUrl = /^data:/i.test(src) ? src : await imageUrlToBase64(src);
              if (dataUrl) {
                let nW = 0, nH = 0;
                try { const p = doc.getImageProperties(dataUrl); nW = p.width; nH = p.height; } catch (e) {}
                let drawW = maxWidth;
                let drawH = (nW && nH) ? (nH * drawW / nW) : 40;
                const maxImgH = pageHeight - pageBuffer - 75;
                if (maxImgH > 0 && drawH > maxImgH) { drawW = drawW * maxImgH / drawH; drawH = maxImgH; }
                if (x > initialX) { x = initialX; y += 5; }
                if (y + drawH > pageHeight - pageBuffer) { y = await addNewPage(); }
                const fmt = /image\/jpe?g/i.test(dataUrl) ? 'JPEG' : 'PNG';
                try { doc.addImage(dataUrl, fmt, initialX, y, drawW, drawH); } catch (e) {}
                y += drawH + 3; x = initialX;
              }
            }
            continue;
          }

          let newStyle = { ...styleStack[styleStack.length - 1] };
          let isBlock = false;
          switch (tagName) {
            case 'STRONG': case 'B': newStyle.style = 'bold'; break;
            case 'EM': case 'I': newStyle.style = 'italic'; break;
            case 'BR': x = initialX; y += 5; break;
            case 'P': if (x > initialX) { y += 8; x = initialX; } isBlock = true; break;
            case 'LI':
              if (x > initialX) y += 6;
              x = initialX; applyStyle();
              doc.text('• ', x, y); x += doc.getTextWidth('• '); isBlock = true; break;
            case 'UL': case 'OL': if (x > initialX) { y += 8; x = initialX; } isBlock = true; break;
            default: break;
          }
          styleStack.push(newStyle);
          await parseNodes(node.childNodes);
          styleStack.pop();
          if (isBlock) { x = initialX; y += 5; }
        }
      }
    };
    const tmp = document.createElement('div');
    tmp.innerHTML = htmlString;
    await parseNodes(tmp.childNodes);
    return y;
  };

  // base url for sign images — read from AppConst via closure-safe import
  function AppConstBase() {
    try { return (AppConst && AppConst.BASE_URL) || ''; } catch (e) { return ''; }
  }




  const stampPage = () => { pageDept[doc.internal.getNumberOfPages()] = currentGroupDept; };
  // ---- page machinery ----
   

   const addNewPage = async () => {
    doc.addPage();
    const pg = doc.internal.getNumberOfPages();
    doc.setPage(pg);
    currentPageNumber = pg;
    lastPrintedDeptOnCurrentPage = null;
    if (currentTestStamp && !pageStamp[pg]) pageStamp[pg] = currentTestStamp;
    const y = printPatientDetails(PATIENT_BOX_Y, false);
    stampPage();
    return y;
  };
 



  const hasSpace = (n) => yPos + n <= SAFE_BOTTOM;





  const printDepartmentHeader = (deptName) => {
    yPos += 4;
    doc.setFillColor(...THEME.headerBg);
    doc.setDrawColor(...THEME.border);
    doc.setLineWidth(0.2);
    const r = THEME.outline.deptHeader ? (Number(THEME.outline.radius) || 0) : 0;
    const style = THEME.outline.deptHeader ? 'FD' : 'F';
    if (r > 0) doc.roundedRect(10, yPos - 5, 190, 8, r, r, style);
    else doc.rect(10, yPos - 5, 190, 8, style);
    doc.setFontSize(THEME.font.deptHeader).setFont('helvetica', 'bold').setTextColor(...THEME.headerTxt);
    doc.text(deptName, 105, yPos, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    yPos += 8;
    lastPrintedDeptOnCurrentPage = deptName;
  };

   
    const CONTENT_START_Y = printPatientDetails(PATIENT_BOX_Y, false);
  yPos = CONTENT_START_Y;
  stampPage();

  const C = THEME.columns;
  const estimateRowHeight = (row) => {
    if (!row || !Array.isArray(row)) return 6;
    if (row.length === 1 && row[0]?.colSpan) return 8;
    const rc = row[3];
    const rt = typeof rc === 'string' ? rc : (rc?.content || '');
    const lines = Math.max(1, Math.ceil(String(rt).length / 30));
    return Math.max(6, lines * 4 + 2);
  };

  const drawMainTable = async (rows, needsHead, currentDept, reserveAfter = 0, pendingDept = null) => {
    if (rows.length <= 1) return;
    const deptHeaderHt = pendingDept ? 12 : 0;
    const rowsHeight = rows.reduce((s, r) => s + estimateRowHeight(r), 0);
    const est = deptHeaderHt + rowsHeight + (needsHead ? 10 : 0) + reserveAfter;
        const atTop = yPos <= CONTENT_START_Y + 2;
    if (!hasSpace(est) && !atTop) {
      yPos = await addNewPage();
      const d = pendingDept || currentDept;
      if (d) printDepartmentHeader(d);
      needsHead = true;
    } else if (pendingDept) {
      printDepartmentHeader(pendingDept);
    }
       const tableStartY = yPos;
    const startPageNo = doc.internal.getNumberOfPages();
    // this test really is drawing on this page — take the later time if shared
       // first test to draw on this page sets its time; later tests don't steal it
    if (currentTestStamp && !pageStamp[startPageNo]) {
      pageStamp[startPageNo] = currentTestStamp;
    }
    await autoTable(doc, {
      startY: yPos,
      head: needsHead ? [['TEST NAME', 'RESULT', 'UNITS', 'NORMAL RANGES', 'METHOD']] : null,
      body: rows,
      theme: 'plain',
      headStyles: { fillColor: THEME.headerBg, textColor: THEME.headerTxt, fontStyle: 'bold', fontSize: THEME.font.tableHead },
      styles: { fontSize: THEME.font.tableBody, cellPadding: 0.8, valign: 'middle', minCellHeight: 7, textColor: [0, 0, 0] },
      columnStyles: {
        0: { cellWidth: C.testName.width, halign: C.testName.halign },
        1: { cellWidth: C.result.width, halign: C.result.halign, ...(fontOk ? { font: 'DejaVu' } : {}) },
        2: { cellWidth: C.unit.width, halign: C.unit.halign },
        3: { cellWidth: C.range.width, halign: C.range.halign },
        4: { cellWidth: C.method.width, halign: C.method.halign }
      },
          margin: { left: 10, right: 10, top: TOP_SAFE_Y, bottom: SIGNATURE_AREA_HEIGHT + reserveAfter },
      didDrawPage: (data) => {
        if (data.pageNumber > currentPageNumber) {
          currentPageNumber = data.pageNumber;
          lastPrintedDeptOnCurrentPage = null;
         
          
          printPatientDetails(PATIENT_BOX_Y, false);
          stampPage();
          const _pg = doc.internal.getNumberOfPages();
          if (currentTestStamp && !pageStamp[_pg]) pageStamp[_pg] = currentTestStamp;
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.row.index === 0 && data.cell.raw?.isMainTest) {
          doc.setDrawColor(...THEME.border); doc.setLineWidth(0.2);
          const tw = doc.getTextWidth(data.cell.text[0]);
          const ly = data.cell.y + data.cell.height - 1.5;
          doc.line(data.cell.x + 2, ly, data.cell.x + 2 + tw, ly);
        }
        if (data.section === 'body' && data.column.index === 1 && data.cell.raw?._flag) {
          const flag = data.cell.raw._flag;
          const numberText = String(data.cell.text[0] || '');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(THEME.font.tableBody);
          const numW = doc.getTextWidth(numberText);
          const textX = data.cell.x + data.cell.padding('left');
          const baselineY = data.cell.y + data.cell.height / 2 + 1;
          doc.setDrawColor(...flag.color); doc.setLineWidth(0.2);
          doc.line(textX, baselineY + 1, textX + numW, baselineY + 1);
          if (fontOk) doc.setFont('DejaVu', 'normal');
          doc.setTextColor(...flag.color);
          doc.text(flag.label, textX + numW + 1.5, baselineY);
          doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
        }
      },
    });
    const endY = doc.lastAutoTable?.finalY ?? yPos;
    if (doc.internal.getNumberOfPages() === startPageNo) {
      drawOutline(doc, THEME.outline.testTable, 10, tableStartY, 190, endY - tableStartY);
    }
    yPos = endY + 1;
  };

  const departmentMap = {};
  (departs || []).forEach(d => { departmentMap[d.cat_id] = d.cat_name; });
  const getDeptName = (test) => {
    if (test.params && test.params.length > 0) return departmentMap[test.params[0].test_cat] || 'HEMATOLOGY';
    return 'HEMATOLOGY';
  };

  const isRadiologyTest = (test) => {
    const dn = getDeptName(test);
    if (dn && String(dn).toLowerCase() === 'radiology') return true;
    return !!(test.params?.[0] && String(test.params[0].test_cat) === '60');
  };

  const hasAnyPrintableContent = (test) => {
    if (!test.params || test.params.length === 0) return false;
    return test.params.some(p => {
      const rn = String(p.test_param_name || '').trim();
      const star = /^\*.+\*$/.test(rn);
      if (p.test_param_type === 'Editor') return p.result && String(p.result).trim() !== '';
      if (star) return false;
      return p.result !== undefined && p.result !== null && String(p.result).trim() !== '';
    });
  };



  // const sortedTests = [...(tests || [])].sort((a, b) => {
  //   const da = getDeptName(a).toUpperCase(), db = getDeptName(b).toUpperCase();
  //   return da < db ? -1 : da > db ? 1 : 0;
  // });




  const estimateHtmlHeight = (htmlStr) => {
    if (!htmlStr) return 0;
    const d = document.createElement('div'); d.innerHTML = String(htmlStr).trim();
    const plain = d.innerText.replace(/\u00A0/g, ' ').trim();
    const hasTable = !!d.querySelector('table');
    if (!plain && !hasTable) return 0;
    const lines = Math.ceil(plain.length / 80) + (hasTable ? 10 : 0);
    return Math.max(lines * 5, 15) + 15;
  };
  const estimateFullTestBlock = (test) => {
    let h = 10 + 8;
    (test.params || []).forEach(p => {
      if (p.test_param_type === 'Editor') h += estimateHtmlHeight(p.result);
      else if (p.result && String(p.result).trim() !== '') h += 6;
    });
    if (test.params?.[0]?.test_sample_type) h += 8;
    h += estimateHtmlHeight(test.interpretation);
    h += (test.impression && String(test.impression).trim()) ? estimateHtmlHeight(test.impression) : 0;
    h += (test.notes && String(test.notes).trim()) ? estimateHtmlHeight(test.notes) : 0;
    return h + 8;
  };
   const usableHeight = SAFE_BOTTOM - CONTENT_START_Y;


  const renderTest = async (test) => {
    stampPage();

    // this test's own reported time (param-level first, then report-level)
    {
      const ts = [];
      (test.params || []).forEach(p => { if (p.reported_at) ts.push(p.reported_at); });
      if (ts.length === 0 && test.report_info?.test_report_datetime) {
        ts.push(test.report_info.test_report_datetime);
      }
            currentTestStamp = ts.length
        ? ts.map(s => new Date(s)).sort((a, b) => b - a)[0]
        : null;
      const pg = doc.internal.getNumberOfPages();
      // first test to claim a page sets its time; later tests on the SAME page don't steal it
      if (currentTestStamp && !pageStamp[pg]) {
        pageStamp[pg] = currentTestStamp;
      }
    }
      
    let deptName = getDeptName(test);
    if (deptName && deptName.toLowerCase() === 'radiology') hasRadiology = true;
    const isRad60 = (deptName && String(deptName).toLowerCase() === 'radiology') || (test.params?.[0] && String(test.params[0].test_cat) === '60');

    let addTableHead = false, pendingDeptHeader = null;
    const blockHeight = estimateFullTestBlock(test);
    const fitsFull = blockHeight <= usableHeight;
      if (fitsFull && !hasSpace(blockHeight) && yPos > CONTENT_START_Y + 2) {
      yPos = await addNewPage(); pendingDeptHeader = deptName; addTableHead = true;
    }
    if (deptName && deptName !== lastPrintedDeptOnCurrentPage) { pendingDeptHeader = deptName; addTableHead = true; }

    let paramsRows = [];
    const mainRow = [{
      content: test.lab_item_test_name || test.test_name || 'Test', colSpan: 5,
      styles: { fontStyle: 'bold', fontSize: THEME.font.testName, textColor: [0, 0, 0], halign: C.testName.halign, cellPadding: { top: 2, bottom: 2, left: 2 } },
      isMainTest: true
    }];
    paramsRows.push(mainRow);
    let hasResultInTable = false;

    for (const param of test.params) {
      const rawName = String(param.test_param_name || '');
      const star = /^\*.+\*$/.test(rawName.trim());
      const pdfName = star ? rawName.trim().replace(/^\*(.+)\*$/, '$1') : rawName;

      if (param.test_param_type === 'Editor') {
        if (hasResultInTable) {
          await drawMainTable(paramsRows, addTableHead, deptName, 0, pendingDeptHeader);
          pendingDeptHeader = null; paramsRows = [mainRow]; hasResultInTable = false; addTableHead = false;
        }
        if (pendingDeptHeader) {
          if (!hasSpace(20)) yPos = await addNewPage();
          printDepartmentHeader(pendingDeptHeader); pendingDeptHeader = null;
        }
        if (!hasSpace(12)) {
          yPos = await addNewPage();
          if (deptName && deptName !== lastPrintedDeptOnCurrentPage) printDepartmentHeader(deptName);
        }
        yPos += 1;
        const title = isRad60 ? String(test.lab_item_test_name || pdfName) : pdfName;
        doc.setFontSize(isRad60 ? (THEME.font.testName || 12) : THEME.font.editorTitle);
        doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 0, 0);
        const tW = doc.getTextWidth(title), cx = pageWidth / 2;
        doc.text(title, cx, yPos, { align: 'center' });
        doc.setDrawColor(...THEME.border); doc.setLineWidth(0.3);
        doc.line(cx - tW / 2, yPos + 1, cx + tW / 2, yPos + 1);
        yPos += 6;

        const editorContent = String(param.result || '').trim();
        if (editorContent) {
          const tmp = document.createElement('div'); tmp.innerHTML = editorContent;
          for (const node of Array.from(tmp.childNodes)) {
            if (!hasSpace(8)) {
              yPos = await addNewPage();
              if (deptName && deptName !== lastPrintedDeptOnCurrentPage) printDepartmentHeader(deptName);
            }
            let tableEl = null;
            if (node.nodeType === 1) {
              const tag = node.tagName.toUpperCase();
              if (tag === 'TABLE') tableEl = node;
              else if (tag === 'FIGURE' && node.querySelector('table')) tableEl = node.querySelector('table');
              else { const it = node.querySelector && node.querySelector('table'); if (it) tableEl = it; }
            }
            if (tableEl) {
              const tblRows = tableEl.querySelectorAll('tr');
              const estH = Math.max(12, (tblRows.length || 1) * 8 + 10);
              if (!hasSpace(estH)) {
                yPos = await addNewPage();
                if (deptName && deptName !== lastPrintedDeptOnCurrentPage) printDepartmentHeader(deptName);
              }
              try {
                autoTable(doc, {
                  startY: yPos, html: tableEl, theme: 'grid',
                  styles: {
                    fontSize: 9, cellPadding: 2, valign: 'middle',
                    textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2,
                    fillColor: false, 
                    font: fontOk ? 'DejaVu' : 'helvetica'   // <-- add this
                  },
                  headStyles: { textColor: [0, 0, 0],  fillColor: false, fontStyle: 'bold' },
                  didParseCell: (data) => {
                    data.cell.styles.fillColor = false;   // kill any inherited HTML background
                  },
                                   margin: { left: 14, right: 14, top: TOP_SAFE_Y, bottom: SIGNATURE_AREA_HEIGHT },
                  didDrawPage: (data) => {
                    if (data.pageNumber > currentPageNumber) {
                      currentPageNumber = data.pageNumber; lastPrintedDeptOnCurrentPage = null;
                      printPatientDetails(PATIENT_BOX_Y, false);
                      stampPage();
                      const _pg = doc.internal.getNumberOfPages();
                      if (currentTestStamp && !pageStamp[_pg]) pageStamp[_pg] = currentTestStamp;
                    }
                  }
                });
                yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 4 : yPos + 17;
              } catch (e) {}
            } else {
              const chunk = (node.outerHTML !== undefined) ? node.outerHTML : node.nodeValue;
              if (!chunk || String(chunk).trim() === '') continue;
              yPos = await drawHtmlContent(chunk, 16, yPos, {
                maxWidth: 178,
                addNewPage: async () => {
                  const ny = await addNewPage();
                  if (deptName && deptName !== lastPrintedDeptOnCurrentPage) printDepartmentHeader(deptName);
                  return ny;
                },
                pageBuffer: SIGNATURE_AREA_HEIGHT
              });
              yPos += 1;
            }
          }
        } else yPos += 2;
        yPos += 3;
        continue;
      }

      const hasResult = param.result !== undefined && param.result !== null && String(param.result).trim() !== '';
      if (!hasResult && !star) continue;
      let resultVal = hasResult ? String(param.result).trim() : '';
      let resultStyles = { halign: THEME.columns.result.halign, textColor: [0, 0, 0], ...(fontOk ? { font: 'DejaVu' } : {}) };
      const rangeValue = formatCellValue(get_param_val(param));
      if (hasResult) {
        const flag = evaluateFlagSmart(resultVal, rangeValue, patient);
        if (flag) resultStyles = { ...resultStyles, textColor: flag.color, fontStyle: 'bold', font: 'helvetica' };
        if (param.is_bold === true) resultStyles = { ...resultStyles, fontStyle: 'bold', textColor: THEME.flags.boldColor, font: 'helvetica' };
      }
      const flagForCell = hasResult ? evaluateFlagSmart(resultVal, rangeValue, patient) : null;
      paramsRows.push([
        { content: pdfName, styles: star ? { fontStyle: 'bold' } : {} },
        { content: resultVal || '', styles: resultStyles, _flag: flagForCell },
        param.test_range_type || '',
        genderRangeDisplay(rangeValue, patient) || '',
        param.test_param_method || ''
      ]);
      hasResultInTable = true;
    }

    const hasSampleType = !((deptName && String(deptName).toLowerCase() === 'radiology') || (test.params[0] && String(test.params[0].test_cat) === '60')) && test.params[0]?.test_sample_type;
    const sampleReserve = hasSampleType ? 15 : 0;
    if (hasResultInTable) {
      await drawMainTable(paramsRows, addTableHead, deptName, sampleReserve, pendingDeptHeader);
      pendingDeptHeader = null;
    }

    const isRadDept = (deptName && String(deptName).toLowerCase() === 'radiology') || (test.params[0] && String(test.params[0].test_cat) === '60');
    if (!isRadDept && test.params[0]?.test_sample_type) {
      if (!hasSpace(8)) {
        yPos = await addNewPage();
        if (deptName && deptName !== lastPrintedDeptOnCurrentPage) printDepartmentHeader(deptName);
      }
      autoTable(doc, {
        startY: yPos,
        body: [[{ content: `Sample Type : ${test.params[0]?.test_sample_type}`, colSpan: 5, styles: { fontStyle: 'bold', textColor: [80, 80, 80] } }]],
        theme: 'plain', styles: { fontSize: 8, cellPadding: 0.5, valign: 'middle', minCellHeight: 4 },
        margin: { left: 14, right: 14, top: TOP_SAFE_Y, bottom: SIGNATURE_AREA_HEIGHT },
      });
      yPos = doc.lastAutoTable.finalY + 4;
    } else yPos += 2;

    const drawBorderedSection = async (title, htmlContent) => {
      if (!htmlContent) return;
      const BOX_LEFT = SIDE_MARGIN;
      const PW = pageWidth, BOX_WIDTH = PW - BOX_LEFT * 2, PAD = 2.5;
      const CONTENT_X = BOX_LEFT + PAD, CONTENT_MARGIN = BOX_LEFT + PAD, CONTENT_W = PW - CONTENT_X - CONTENT_MARGIN;
      const tmp = document.createElement('div'); tmp.innerHTML = String(htmlContent || '').trim();
      const plain = tmp.innerText.replace(/\u00A0/g, ' ').trim();
      const hasTable = !!tmp.querySelector('table');
      if (!plain && !hasTable) return;
      const startPage = doc.internal.getNumberOfPages();
      const boxTop = yPos;
      if (title && String(title).trim()) {
        if (!hasSpace(12)) yPos = await addNewPage();
        doc.setFontSize(THEME.font.sectionTitle).setFont('helvetica', 'bold').setTextColor(...THEME.headerTxt).text(String(title), CONTENT_X, yPos + 5);
        doc.setTextColor(0, 0, 0); yPos += 11;
      } else { if (!hasSpace(6)) yPos = await addNewPage(); yPos += 2; }
      yPos += 2;

      for (const node of Array.from(tmp.childNodes || [])) {
        if (!hasSpace(8)) yPos = await addNewPage();
        let tableEl = null;
        if (node.nodeType === 1) {
          const tag = node.tagName.toUpperCase();
          if (tag === 'TABLE') tableEl = node;
          else if (tag === 'FIGURE' && node.querySelector('table')) tableEl = node.querySelector('table');
          else { const it = node.querySelector && node.querySelector('table'); if (it) tableEl = it; }
        }
        if (tableEl) {
          const allRows = Array.from(tableEl.querySelectorAll('tr'));
          let colCount = 0;
          allRows.forEach(tr => { let c = 0; tr.querySelectorAll('th, td').forEach(cell => { c += parseInt(cell.getAttribute('colspan') || '1', 10); }); if (c > colCount) colCount = c; });
          let columnStyles = {};
          if (colCount > 0) {
            const fcw = Math.min(CONTENT_W * 0.22, 40);
            const rw = (CONTENT_W - fcw - 1) / (colCount - 1 || 1);
            columnStyles[0] = { cellWidth: fcw };
            for (let c = 1; c < colCount; c++) columnStyles[c] = { cellWidth: rw };
          }
          if (node !== tableEl) {
            const wc = node.cloneNode(true); const it = wc.querySelector('table'); if (it) it.remove();
            const bh = wc.innerHTML;
            if (bh && String(bh).trim()) {
              yPos = await drawHtmlContent(bh, CONTENT_X, yPos, { maxWidth: CONTENT_W, addNewPage: async () => await addNewPage(), pageBuffer: TEXT_PAGE_BUFFER });
              yPos += 2;
            }
          }
          const tblRows = tableEl.querySelectorAll('tr');
          const estH = Math.max(12, (tblRows.length || 1) * 8 + 10);
          if (!hasSpace(estH)) yPos = await addNewPage();
          yPos += 2.5;
          try {
            autoTable(doc, {
              startY: yPos, html: tableEl, theme: 'grid', tableWidth: CONTENT_W, columnStyles,
              styles: {
                fontSize: THEME.font.tableBody, cellPadding: 1.5, valign: 'middle',
                overflow: 'linebreak', textColor: [0, 0, 0], lineColor: THEME.border, lineWidth: 0.2,
                fillColor: false,                                   // <-- add
                font: fontOk ? 'DejaVu' : 'helvetica'
              },
              headStyles: {
                fontSize: THEME.font.tableHead, textColor: THEME.headerTxt,
                fontStyle: 'bold', overflow: 'linebreak',
                fillColor: false,                                   // <-- was THEME.headerBg
                font: (fontOk && boldOk) ? 'DejaVu' : 'helvetica'
              },
              didParseCell: (data) => {
                data.cell.styles.fillColor = false;                 // <-- kill inherited HTML background on every cell
              },
                          margin: { left: CONTENT_X, right: CONTENT_MARGIN, top: TABLE_TOP_MARGIN, bottom: TABLE_BOTTOM_MARGIN },
              didDrawPage: (data) => {
                if (data.pageNumber > currentPageNumber) {
                  currentPageNumber = data.pageNumber; lastPrintedDeptOnCurrentPage = null;
                  printPatientDetails(PATIENT_BOX_Y, false);
                  stampPage();
                  const _pg = doc.internal.getNumberOfPages();
                  if (currentTestStamp && !pageStamp[_pg]) pageStamp[_pg] = currentTestStamp;
                }
              }
            });
            yPos = doc.lastAutoTable ? doc.lastAutoTable.finalY + 4 : yPos + 17;
          } catch (e) {}
        } else {
          const chunk = (node.outerHTML !== undefined) ? node.outerHTML : node.nodeValue;
          if (!chunk || String(chunk).trim() === '') continue;
          yPos = await drawHtmlContent(chunk, CONTENT_X, yPos, { maxWidth: CONTENT_W, addNewPage: async () => await addNewPage(), pageBuffer: TEXT_PAGE_BUFFER });
          yPos += 1;
        }
      }
      yPos += PAD;
      const endPage = doc.internal.getNumberOfPages();
      if (THEME.outline.interpBox) {
        if (startPage === endPage) {
          drawOutline(doc, THEME.outline.interpBox, BOX_LEFT, boxTop, BOX_WIDTH, Math.max(10, yPos - boxTop));
        } else {
          const cur = doc.internal.getCurrentPageInfo().pageNumber;
          for (let pg = startPage; pg <= endPage; pg++) {
            doc.setPage(pg);
            let segTop, segBot;
            if (pg === startPage) { segTop = boxTop; segBot = BOX_BOTTOM; }
            else if (pg === endPage) { segTop = TOP_SAFE_Y + 5; segBot = Math.min(yPos, BOX_BOTTOM); }
            else { segTop = TOP_SAFE_Y + 5; segBot = BOX_BOTTOM; }
            drawOutline(doc, THEME.outline.interpBox, BOX_LEFT, segTop, BOX_WIDTH, Math.max(6, segBot - segTop));
          }
          doc.setPage(cur);
        }
      }
      yPos += 3;
    };

    await drawBorderedSection('', test.interpretation);
    await drawBorderedSection('Impression', test.impression);
    await drawBorderedSection('Notes:', test.notes);

    if (test.test_image && String(test.test_image).trim()) {
      const imgData = /^data:/i.test(test.test_image) ? test.test_image : await imageUrlToBase64(test.test_image);
      if (imgData) {
        let nW = 0, nH = 0;
        try { const p = doc.getImageProperties(imgData); nW = p.width; nH = p.height; } catch (e) {}
        const boxLeft = SIDE_MARGIN;
        const drawW = pageWidth - boxLeft * 2;
        let drawH = (nW && nH) ? (nH * drawW / nW) : 100;
        const maxH = BOX_BOTTOM - TOP_SAFE_Y;
        let finalW = drawW;
        if (drawH > maxH) { finalW = drawW * maxH / drawH; drawH = maxH; }
        if (!hasSpace(drawH + 4)) yPos = await addNewPage();
        const fmt = /image\/jpe?g/i.test(imgData) ? 'JPEG' : 'PNG';
        try { doc.addImage(imgData, fmt, boxLeft, yPos, finalW, drawH); yPos += drawH + 4; } catch (e) {}
      }
    }
  };   // ✅ END of renderTest

  // ---- split into pathology (everything not cat 60) and radiology (cat 60) ----
  const printable = (tests || []).filter(hasAnyPrintableContent);
  const pathologyTests = printable.filter(t => !isRadiologyTest(t))
    .sort((a, b) => getDeptName(a).toUpperCase().localeCompare(getDeptName(b).toUpperCase()));
  const radiologyTests = printable.filter(t => isRadiologyTest(t))
    .sort((a, b) => getDeptName(a).toUpperCase().localeCompare(getDeptName(b).toUpperCase()));
  hasRadiology = radiologyTests.length > 0;

  // ---- PATHOLOGY BLOCK ----
  currentGroupDept = 'path';
  stampPage();
  for (const t of pathologyTests) {
    await renderTest(t);
  }

  // ---- RADIOLOGY BLOCK (fresh page, never shares a page with pathology) ----
   if (radiologyTests.length > 0) {
    if (pathologyTests.length > 0) {
      currentTestStamp = null;      // don't carry pathology's time onto the radiology page
      yPos = await addNewPage();
    }
    currentGroupDept = 'rad';
    stampPage();
    for (const t of radiologyTests) {
      await renderTest(t);
    }
  }

  

  

  if (!hasSpace(12)) yPos = await addNewPage();
  doc.setFontSize(THEME.font.endOfReport).setFont('helvetica', 'bold');
  doc.text('*** End of Report ***', pageWidth / 2, yPos + 8, { align: 'center' });





      const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    currentTestStamp = pageStamp[i] || null;
    addHeaderAndFooter();
    printPatientDetails(PATIENT_BOX_Y);
    const dept = pageDept[i] || 'path';
    const sd = dept === 'rad' ? signData['60'] : signData['32'];
    await addSignatures(sd);


    doc.setFontSize(8).setTextColor(0, 0, 0);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 20, { align: 'right' });
  }

  return doc;
}